// 브랜드 링크 + PDF export 전역 관리 — 전 크리에이터 발급/추출 이력 (spec §5-2).
import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  fmtDateTime,
  issueBrandLink,
  listBrandLinks,
  listPdfExports,
  restoreBrandLink,
  revokeBrandLink,
  type BrandLinkItem,
  type PdfExportItem,
} from './brandLinksApi'

const STATUS_LABEL: Record<BrandLinkItem['status'], string> = {
  active: '활성',
  expired: '만료',
  revoked: '회수',
}

type Tab = 'links' | 'pdf'

export function BrandLinksPage() {
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'pdf' ? 'pdf' : 'links'
  const [items, setItems] = useState<BrandLinkItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [pdfItems, setPdfItems] = useState<PdfExportItem[]>([])
  const [pdfNextCursor, setPdfNextCursor] = useState<string | null>(null)
  const [status, setStatus] = useState<'all' | 'active' | 'expired' | 'revoked'>('all')
  const [q, setQ] = useState('')
  const [busyToken, setBusyToken] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const setTab = (next: Tab) => {
    const sp = new URLSearchParams(params)
    if (next === 'pdf') sp.set('tab', 'pdf')
    else sp.delete('tab')
    setParams(sp, { replace: true })
    setError(null)
  }

  const load = useCallback(
    async (cursor?: string) => {
      try {
        const page = await listBrandLinks({ status, q: q.trim() || undefined, cursor })
        setItems((prev) => (cursor ? [...prev, ...page.items] : page.items))
        setNextCursor(page.nextCursor)
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.')
      }
    },
    [status, q],
  )
  const loadPdf = useCallback(
    async (cursor?: string) => {
      try {
        const page = await listPdfExports({ q: q.trim() || undefined, cursor })
        setPdfItems((prev) => (cursor ? [...prev, ...page.items] : page.items))
        setPdfNextCursor(page.nextCursor)
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.')
      }
    },
    [q],
  )
  useEffect(() => {
    if (tab === 'links') void load()
    else void loadPdf()
  }, [tab, load, loadPdf])

  const copy = async (item: BrandLinkItem) => {
    try {
      await navigator.clipboard.writeText(item.url)
      setCopiedToken(item.token)
      setTimeout(() => setCopiedToken(null), 1500)
    } catch {
      /* noop */
    }
  }
  const revoke = async (item: BrandLinkItem) => {
    setBusyToken(item.token)
    try {
      await revokeBrandLink(item.token)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : '회수에 실패했습니다.')
    } finally {
      setBusyToken(null)
    }
  }
  // 회수 취소 — 같은 URL 재활성화, 만료 새로 30일 (서버가 부여)
  const restore = async (item: BrandLinkItem) => {
    setBusyToken(item.token)
    try {
      await restoreBrandLink(item.token)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : '재활성화에 실패했습니다.')
    } finally {
      setBusyToken(null)
    }
  }
  // 재발급 — 같은 크리에이터·수신처로 새 링크 (스냅샷은 현재 데이터로 새로 생성, spec §5-2)
  const reissue = async (item: BrandLinkItem) => {
    setBusyToken(item.token)
    try {
      await issueBrandLink(item.creatorId, item.brandName)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : '재발급에 실패했습니다.')
    } finally {
      setBusyToken(null)
    }
  }
  // 재다운로드 — 추출 시점 스냅샷 그대로 열림 (autoPrint 내장). 열람 후 목록 카운트 갱신.
  const openPdf = (item: PdfExportItem) => {
    window.open(new URL(item.fileUrl, window.location.origin).href, '_blank', 'noopener')
    setTimeout(() => void loadPdf(), 1200)
  }

  const tabBtn = (key: Tab, label: string) => (
    <button
      className={
        tab === key
          ? 'border-b-2 border-brand-blue pb-2 font-text text-[13px] font-medium text-ink-800'
          : 'border-b-2 border-transparent pb-2 font-text text-[13px] text-fg-3'
      }
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  )

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10">
      {/* 브레드크럼 — 워크스페이스(CreatorsPage)와 **같은 문법**이다. 두 화면이 같은
          카테고리 아래 있는데 뒤로가기 모양이 다르면 어디서 왔는지 매번 다시 읽어야 한다.
          링크 목적지도 동일하게 /creators(목록) — 히스토리를 따라가지 않는 이유는 새 탭으로
          열었을 때 갈 곳이 없기 때문이다. */}
      <div className="flex items-center gap-[11px]">
        <Link
          to="/creators"
          className="inline-flex items-center gap-2 font-text text-[14px] font-medium text-fg-2 hover:text-fg-1"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          For Brand Intelligence Consulting
        </Link>
        <span className="text-ink-300">/</span>
        <span className="font-text text-[14px] font-medium text-fg-1">Shared documents</span>
      </div>
      {/* 이 화면은 링크와 PDF **둘 다** 담는다. 예전 이름 "Brand links"는 하위 탭 하나와
          같아서 부모·자식이 같은 이름이었고, PDF exports 는 이름상 없는 것이 됐다.
          형식이 아니라 "내보낸 산출물"을 가리키는 이름이라 형식이 늘어도 안 바뀐다.
          URL(/creators/brand-links)은 그대로 둔다 — 발급된 링크·북마크를 깨지 않는다. */}
      <h1 className="mt-3 font-display text-2xl font-medium text-ink-800">
        Shared documents<span className="text-brand-blue">.</span>
      </h1>
      <div className="mt-6 flex items-center gap-5 border-b border-line-soft">
        {tabBtn('links', 'Links')}
        {tabBtn('pdf', 'PDF exports')}
      </div>
      <div className="mt-5 flex items-center gap-3">
        {tab === 'links' && (
          <select
            className="h-[34px] border border-line-soft bg-white px-2 font-text text-[13px]"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        )}
        <input
          className="h-[34px] w-[240px] border border-line-soft bg-white px-3 font-text text-[13px]"
          placeholder="Search creator or brand"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <p className="mt-4 font-text text-[13px] text-status-danger" role="alert">{error}</p>}
      {tab === 'links' ? (
        <>
          <table className="mt-5 w-full border-collapse font-text text-[13px]">
            <thead>
              <tr className="border-b border-line-soft text-left text-[11px] tracking-[0.1em] text-fg-3">
                <th className="py-2 pr-3">CREATOR</th>
                <th className="py-2 pr-3">BRAND</th>
                <th className="py-2 pr-3">LINK</th>
                <th className="py-2 pr-3">ISSUED BY</th>
                <th className="py-2 pr-3">CREATED</th>
                <th className="py-2 pr-3">EXPIRES</th>
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2 pr-3">VIEWS</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.token} className="border-b border-line-soft">
                  <td className="py-2.5 pr-3">
                    <Link to={`/creators/${item.creatorId}`} className="font-medium text-ink-800">
                      {item.creatorName}
                    </Link>
                  </td>
                  <td className="py-2.5 pr-3">{item.brandName}</td>
                  <td className="py-2.5 pr-3">
                    <button
                      className="w-[64px] border border-line-soft bg-white py-1 text-center text-[12px]"
                      onClick={() => void copy(item)}
                    >
                      {copiedToken === item.token ? 'Copied' : 'Copy'}
                    </button>
                  </td>
                  <td className="py-2.5 pr-3">{item.createdBy.split('@')[0]}</td>
                  <td className="py-2.5 pr-3 tabular-nums">{fmtDateTime(item.createdAt)}</td>
                  {/* 회수 시 서버가 expiresAt을 회수 시각으로 당기므로 그대로 표시 (사용자 확정) */}
                  <td className="py-2.5 pr-3 tabular-nums">{fmtDateTime(item.expiresAt)}</td>
                  <td className="py-2.5 pr-3">{STATUS_LABEL[item.status]}</td>
                  <td className="py-2.5 pr-3">
                    {item.viewCount}회{item.lastViewedAt ? ` · ${fmtDateTime(item.lastViewedAt)}` : ' · —'}
                  </td>
                  <td className="py-2.5 text-right">
                    {item.status === 'active' && (
                      <button
                        className="border border-line-soft bg-white px-2 py-1 text-[12px]"
                        disabled={busyToken === item.token}
                        onClick={() => void revoke(item)}
                      >
                        Revoke
                      </button>
                    )}
                    {item.status === 'expired' && (
                      <button
                        className="bg-brand-blue px-2 py-1 text-[12px] font-medium text-white"
                        disabled={busyToken === item.token}
                        onClick={() => void reissue(item)}
                      >
                        Reissue
                      </button>
                    )}
                    {item.status === 'revoked' && (
                      <button
                        className="border border-line-soft bg-white px-2 py-1 text-[12px]"
                        disabled={busyToken === item.token}
                        onClick={() => void restore(item)}
                      >
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && !error && (
            <p className="mt-8 font-text text-[13px] text-fg-3">No links issued yet.</p>
          )}
          {nextCursor && (
            <button
              className="mt-5 h-[34px] border border-line-soft bg-white px-4 font-text text-[13px]"
              onClick={() => void load(nextCursor)}
            >
              Load more
            </button>
          )}
        </>
      ) : (
        <>
          <table className="mt-5 w-full border-collapse font-text text-[13px]">
            <thead>
              <tr className="border-b border-line-soft text-left text-[11px] tracking-[0.1em] text-fg-3">
                <th className="py-2 pr-3">CREATOR</th>
                <th className="py-2 pr-3">BRAND</th>
                <th className="py-2 pr-3">EXPORTED BY</th>
                <th className="py-2 pr-3">CREATED</th>
                <th className="py-2 pr-3">DOWNLOADS</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {pdfItems.map((item) => (
                <tr key={item.id} className="border-b border-line-soft">
                  <td className="py-2.5 pr-3">
                    <Link to={`/creators/${item.creatorId}`} className="font-medium text-ink-800">
                      {item.creatorName}
                    </Link>
                  </td>
                  {/* 수신처 미입력 추출 — 범용 문구 커버 (— 표기) */}
                  <td className="py-2.5 pr-3">{item.brandName ?? '—'}</td>
                  <td className="py-2.5 pr-3">{item.createdBy.split('@')[0]}</td>
                  <td className="py-2.5 pr-3 tabular-nums">{fmtDateTime(item.createdAt)}</td>
                  <td className="py-2.5 pr-3">
                    {item.downloadCount}회
                    {item.lastDownloadedAt ? ` · ${fmtDateTime(item.lastDownloadedAt)}` : ' · —'}
                  </td>
                  <td className="py-2.5 text-right">
                    <button
                      className="border border-line-soft bg-white px-2 py-1 text-[12px]"
                      onClick={() => openPdf(item)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pdfItems.length === 0 && !error && (
            <p className="mt-8 font-text text-[13px] text-fg-3">No PDF exports yet.</p>
          )}
          {pdfNextCursor && (
            <button
              className="mt-5 h-[34px] border border-line-soft bg-white px-4 font-text text-[13px]"
              onClick={() => void loadPdf(pdfNextCursor)}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  )
}
